import React, { useMemo, useRef, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { object, string } from 'yup'
import { Trans, useTranslation } from 'react-i18next'

import { FormField, Spacer, Select, Modal, ActionModal, AcknowledgeModal, SelectOption, Text } from '@percent/lemonade'
import { useMutation } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { EditUserModalProps } from './EditUserModal.types'

export function EditUserModal({ openModalData, assignableRoles, onClose, refresh }: EditUserModalProps) {
  const { t } = useTranslation()
  const { iamService } = useServices()
  const [successData, setSuccessData] = useState<{ email: string; role: string } | undefined>(undefined)
  const [isError, setIsError] = useState(false)
  const pendingSubmissionRef = useRef<{ email: string; role: string } | null>(null)

  const selectOptions = useMemo<SelectOption[]>(
    () =>
      assignableRoles.map(role => ({
        label: role.displayName,
        value: role.id,
        description: role.description
      })),
    [assignableRoles]
  )

  const [{ isLoading: isSaving }, { apiFunc: patchRole }] = useMutation(
    iamService.patchRoles,
    () => {
      if (pendingSubmissionRef.current) {
        setSuccessData(pendingSubmissionRef.current)
      }
      onClose()
      refresh()
    },
    () => {
      onClose()
      setIsError(true)
    }
  )

  const formik = useFormik({
    initialValues: { role: openModalData?.roleId ?? '' },
    enableReinitialize: true,
    validationSchema: () =>
      object().shape({
        role: string()
      }),
    onSubmit: ({ role }) => {
      if (!openModalData) return
      const selected = assignableRoles.find(r => r.id === role)
      pendingSubmissionRef.current = {
        email: openModalData.email,
        role: selected?.displayName ?? openModalData.role
      }
      patchRole({
        accountId: openModalData.userId,
        roleId: role
      })
    }
  })

  const { errors, values, touched, handleSubmit, setFieldValue } = formik

  const isUnchanged = values.role === (openModalData?.roleId ?? '')

  const selectedOption = useMemo<SelectOption | undefined>(
    () => selectOptions.find(option => option.value === values.role),
    [selectOptions, values.role]
  )

  const handleCloseSuccessModal = () => setSuccessData(undefined)
  const handleCloseErrorModal = () => setIsError(false)

  return (
    <>
      <Modal open={Boolean(openModalData)} onClose={onClose} aria-labelledby="open-edit-user-modal">
        <ActionModal
          title={t('dialog.editUser.title')}
          primaryButtonText={t('button.saveChanges')}
          secondaryButtonText={t('button.cancel')}
          handleClose={onClose}
          handleSubmit={handleSubmit}
          loading={isSaving}
          disabled={isSaving || isUnchanged}
        >
          <Text fontWeight="medium">{openModalData?.email}</Text>{' '}
          <Text>{t('typography.editUser.role', { role: openModalData?.role })}</Text>
          <Spacer size={6} axis="vertical" />
          <form onSubmit={handleSubmit}>
            <FormikProvider value={formik}>
              <FormField
                label={t('typography.role')}
                status={touched.role && errors.role ? 'danger' : 'default'}
                statusMessage={errors.role}
              >
                <Select
                  key={openModalData?.userId}
                  name="role"
                  placeholder={t('form.selectRole')}
                  onChange={e => {
                    setFieldValue('role', e.value)
                  }}
                  options={selectOptions}
                  defaultValue={selectedOption}
                />
              </FormField>
            </FormikProvider>
          </form>
        </ActionModal>
      </Modal>
      <Modal open={Boolean(successData)} onClose={handleCloseSuccessModal} aria-labelledby="edit-user-success-modal">
        <AcknowledgeModal
          result="positive"
          title={t('dialog.editUser.success.title')}
          description={
            <Trans
              i18nKey="dialog.editUser.success.description"
              values={{ email: successData?.email, role: successData?.role }}
              components={{ b: <strong /> }}
            />
          }
          buttonText={t('button.close')}
          handleClose={handleCloseSuccessModal}
          buttonTestId="edit-user-success-modal-btn"
        />
      </Modal>
      <Modal open={isError} onClose={handleCloseErrorModal} aria-labelledby="edit-user-error-modal">
        <AcknowledgeModal
          result="negative"
          title={t('dialog.editUser.error.title')}
          description={t('dialog.error.description')}
          buttonText={t('button.close')}
          handleClose={handleCloseErrorModal}
          buttonTestId="edit-user-error-modal-btn"
        />
      </Modal>
    </>
  )
}
